import {authDropdownIcons} from '@app/auth/auth-dropdown-icons';
import {stopImpersonatingUserOptions} from '@common/admin/users/users-queries';
import {useLogout} from '@common/auth/requests/use-logout';
import {useAuth} from '@common/auth/use-auth';
import {showHttpErrorToast} from '@common/http/errors/show-http-error-toast';
import {notificationBadgeCount} from '@common/notifications/notification-badge-count';
import {
  ColorSchemeContext,
  ColorSchemePreference,
} from '@common/core/color-scheme-provider';
import {SiteConfigContext} from '@common/core/settings/site-config-context';
import {MenuItemIcon} from '@common/menus/custom-menu';
import {MenuItemConfig} from '@common/menus/menu-config';
import {useCustomMenu} from '@common/menus/use-custom-menu';
import {useNavigate} from '@common/ui/navigation/use-navigate';
import {Dropdown} from '@shadcn/dropdown/dropdown';
import {useMutation} from '@tanstack/react-query';
import {Trans} from '@ui/i18n/trans';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {useSettings} from '@ui/settings/use-settings';
import {
  BellIcon,
  CheckIcon,
  CircleUserIcon,
  LogOutIcon,
  MonitorCogIcon,
  MonitorIcon,
  MoonIcon,
  SunIcon,
} from 'lucide-react';
import {ComponentProps, ReactElement, use, useContext} from 'react';

interface Props {
  children: ReactElement;
  items?: ReactElement<ComponentProps<typeof Dropdown.Item>>[];
  side?: ComponentProps<typeof Dropdown.Content>['side'];
  align?: ComponentProps<typeof Dropdown.Content>['align'];
  accountSettingsLink?: string;
}
export function NavbarAuthMenu({
  children,
  items,
  side,
  align = 'end',
  accountSettingsLink,
}: Props) {
  const {auth} = useContext(SiteConfigContext);
  const logout = useLogout();
  const menu = useCustomMenu('auth-dropdown');
  const {notifications} = useSettings();
  const {user} = useAuth();
  const navigate = useNavigate();
  const impersonator = getBootstrapData().impersonator;
  const stopImpersonating = useMutation(stopImpersonatingUserOptions);
  if (!user) return null;
  const count = notificationBadgeCount(user);
  const hasUnreadNotif = count > 0;

  const notifMenuItem = (
    <Dropdown.Item
      className="md:hidden"
      onClick={() => {
        navigate('/notifications');
      }}
    >
      <BellIcon />
      <Trans message="Notifications" />
      {hasUnreadNotif ? ` (${count})` : undefined}
    </Dropdown.Item>
  );

  const handleItemClick = (item: MenuItemConfig) => {
    let action = item.action ?? '';

    if (action === '/account-settings' && accountSettingsLink) {
      action = accountSettingsLink;
    }

    if (item.type === 'link') {
      window.open(action, '_blank');
    } else {
      navigate(action);
    }
  };

  return (
    <Dropdown.Root>
      {children}
      <Dropdown.Content side={side} align={align} className="min-w-60">
        <div className="px-3 py-2.5">
          <div className="truncate text-sm font-semibold">{user.name}</div>
          {user.email ? (
            <div className="truncate text-xs text-muted-foreground">
              {user.email}
            </div>
          ) : null}
        </div>
        <Dropdown.Separator />
        {menu &&
          menu.items.map(item => (
            <Dropdown.Item key={item.id} onClick={() => handleItemClick(item)}>
              <MenuItemIcon item={item} defaultIcons={authDropdownIcons} />
              <Trans message={item.label} />
            </Dropdown.Item>
          ))}
        {auth?.getUserProfileLink && (
          <Dropdown.Item
            onClick={() => {
              navigate(encodeURI(auth.getUserProfileLink!(user)));
            }}
          >
            <CircleUserIcon />
            <Trans message="Profile page" />
          </Dropdown.Item>
        )}
        {items}
        {notifications?.integrated ? notifMenuItem : undefined}
        <AppearanceSubMenu />
        {impersonator ? (
          <Dropdown.Item
            onClick={() =>
              stopImpersonating.mutate(undefined, {
                onError: err => showHttpErrorToast(err),
              })
            }
          >
            <MonitorCogIcon />
            <Trans message="Back to admin" />
          </Dropdown.Item>
        ) : null}
        <Dropdown.Separator />
        <Dropdown.Item
          variant="destructive"
          onClick={() => {
            logout.mutate();
          }}
        >
          <LogOutIcon />
          <Trans message="Log out" />
        </Dropdown.Item>
      </Dropdown.Content>
    </Dropdown.Root>
  );
}

const appearanceOptions: {
  value: ColorSchemePreference;
  icon: typeof SunIcon;
  label: string;
}[] = [
  {value: 'light', icon: SunIcon, label: 'Light'},
  {value: 'dark', icon: MoonIcon, label: 'Dark'},
  {value: 'system', icon: MonitorIcon, label: 'System'},
];

function AppearanceSubMenu() {
  const {themes} = useSettings();
  const {colorScheme, preference, setColorScheme} = use(ColorSchemeContext);

  if (!themes?.user_change) return null;

  return (
    <Dropdown.Sub>
      <Dropdown.SubTrigger>
        {colorScheme === 'dark' ? <MoonIcon /> : <SunIcon />}
        <Trans message="Appearance" />
      </Dropdown.SubTrigger>
      <Dropdown.SubContent>
        {appearanceOptions.map(option => {
          const Icon = option.icon;
          return (
            <Dropdown.Item
              key={option.value}
              onClick={() => setColorScheme(option.value)}
            >
              <Icon />
              <Trans message={option.label} />
              {preference === option.value ? (
                <CheckIcon className="ms-auto size-4" />
              ) : (
                <span className="ms-auto size-4" />
              )}
            </Dropdown.Item>
          );
        })}
      </Dropdown.SubContent>
    </Dropdown.Sub>
  );
}
